<script>
   /*************************************************************/
   /* AppPlots component for train tab                          */
   /* -----------------------------------                       */
   /* defines plot components, controls and parameters          */
   /*************************************************************/

   import { getDefaults } from 'svelte-controls-basic';
   import { Container, Button } from 'svelte-controls-basic';
   import { getCI } from '@mdatools-apps/shared/utils';

   // global plot router
   import PlotsRouter from '@mdatools-apps/shared/plots/PlotsRouter.svelte';

   // import plot components
   import PlotAcceptance from '@mdatools-apps/shared/plots/PlotAcceptance.svelte';
   import PlotDistances from '@mdatools-apps/shared/plots/PlotDistances.svelte';
   import PlotDoF from '@mdatools-apps/shared/plots/PlotDoF.svelte';
   import PlotExtremes from '@mdatools-apps/shared/plots/PlotExtremes.svelte';
   import PlotSensitivity from '@mdatools-apps/shared/plots/PlotClassStat.svelte';
   import PlotScores from '@mdatools-apps/shared/plots/PlotScores.svelte';
   import PlotLoadings from '@mdatools-apps/shared/plots/PlotLoadings.svelte';

   // import plot controls components
   import { CAT_COLORS } from '@mdatools-apps/shared/plots/Defaults.js';
   import { getPlotAcceptanceControls, getPlotDistancesControls, getPlotDoFControls, getPlotExtremesControls,
            getPlotClassStatControls, getPlotScoresControls, getPlotLoadingsControls } from '@mdatools-apps/shared/plots/Controls.svelte.js';

   let {
      model,
      simca,
      calres,
      pvres,
      calset,
      pvset,
      markOutlier,
      showContributions
   } = $props();

   // because most of the plot components expect JSON with results
   // assuming there is more than one result object we create it
   // same for the data
   const res = {'cal': calres.pca, 'pv': pvres.pca};
   const data = {'cal': calset, 'pv': pvset};

   // this is derived as simca results can change without reloading the component
   const classres = $derived({'cal': calres.simca, 'pv': pvres.simca});

   // colors for acceptance plot
   const classColmap = {'regular' : CAT_COLORS.regular, 'extreme': CAT_COLORS.extreme, 'outlier': CAT_COLORS.outlier};


   // JSON with plot components (scope is needed for providing correct properties)
   const plots = $derived({
      acceptance: {label: 'Acceptance', scope: 'objects', component: PlotAcceptance, buttons: buttons2},
      distances: {label: 'Distances', scope: 'objects', component: PlotDistances, buttons: buttons2},
      dof: {label: 'DoF', scope: 'stats', component: PlotDoF},
      extremes: {label: 'Extremes', scope: 'stats', component: PlotExtremes},
      sensitivity: {label: 'Sensitivity', scope: 'stats', component: PlotSensitivity},
      loadings: {label: 'Loadings', scope: 'stats', component: PlotLoadings},
      scores: {label: 'Scores', scope: 'objects', component: PlotScores, buttons: buttons1},
   })

   // JSON with plots controls (as Widget properties)
   const controls = $state({
      acceptance: getPlotAcceptanceControls({'cal': calres.simca, 'pv': pvres.simca}),
      distances: getPlotDistancesControls(res),
      dof: getPlotDoFControls(),
      extremes: getPlotExtremesControls(['cal', 'pv', 'both']),
      sensitivity: getPlotClassStatControls(classres),
      scores: getPlotScoresControls(res),
      loadings: getPlotLoadingsControls(model),
   })

   // the factory marks these two 'hidden', which is where they start and not something the data
   // rules out - nothing here makes them impossible, they simply wait for the right selection.
   // 'hidden' is OR-ed with the rules below and would keep them hidden for good, so it is cleared
   // and their visibility is left entirely to 'hiddenWhen'
   controls.scores.colorByVar.hidden = false;
   controls.scores.confEllipse.hidden = false;

   // which controls do not apply to what the user has currently selected. the factories above set
   // 'hidden' for what the data rules out, and Widget combines the two with OR, so a rule here can
   // never bring back a control the data has excluded
   controls.scores.comp1.hiddenWhen       = (p) => p.plotType !== 'p';
   controls.scores.showEllipse.hiddenWhen = (p) => p.plotType !== 'p';
   controls.scores.colorBy.hiddenWhen     = (p) => p.plotType === 'l' || p.plotType === 'b';
   controls.scores.colorByVar.hiddenWhen  = (p) => p.colorBy !== 'variable';
   controls.scores.confEllipse.hiddenWhen = (p) => !(p.colorBy === 'groups' && p.plotType === 'p');
   controls.loadings.comp1.hiddenWhen     = (p) => p.plotType !== 'p';

   // JSON with currently selected plots parameters
   let params = $state({
      acceptance: getDefaults(controls.acceptance),
      distances: getDefaults(controls.distances),
      dof: getDefaults(controls.dof),
      extremes: getDefaults(controls.extremes),
      sensitivity: getDefaults(controls.sensitivity),
      scores: getDefaults(controls.scores),
      loadings: getDefaults(controls.loadings),
   });


   // add confidence interval values for sensitivity plot
   $effect(() => {
      const ci = {};
      if (calres.simca.nMembers > 0) {
         ci['sens'] = getCI(calres.simca.nMembers, simca.alpha);
      }
      params.sensitivity.ci = ci;
   });


   // add additional fixed parameters for the acceptance plot
   $effect(() => {
      params.acceptance['ncomp'] = simca.ncomp;
      params.acceptance['title'] = `Acceptance (A = ${simca.ncomp})`;
      params.acceptance['limType'] = simca.limType;
      params.acceptance['alpha'] = simca.alpha;
      params.acceptance['gamma'] = simca.gamma;
   });

   // if limType or result objects changed make updates to acceptance plot fixed parameters
   $effect(() => {
      if (simca.limType) {
         let simcares = {'cal': calres.simca, 'pv': pvres.simca};
         const res = simcares[params.acceptance.resID].v[simca.ncomp - 1];

         // this is needed for correct color grouping, we simply provide
         // colorby values manually, based on classification outcomes
         params.acceptance['colorByObj'] = {
            classColmap,
            labels: [`regular: ${res.rolsNum.regular}`, `extreme: ${res.rolsNum.extreme}`, `outliers: ${res.rolsNum.outlier}`],
            values: res.rols
         }
      }
   });

   // the resID options are the one thing here which is still driven imperatively. 'Widget' spreads
   // 'props' at render, so a getter on 'options' would work too; a plain effect on a '$state'
   // descriptor is the clearer of the two. this is why 'controls' above stays '$state' in this tab
   // while the other two hold a plain object
   $effect(() => {
      if (params.scores.colorBy === 'variable') {
         if (params.scores.resID === 'both') params.scores.resID = 'cal';
         controls.scores.resID.props.options = ['cal', 'pv']
      } else {
         controls.scores.resID.props.options = ['cal', 'pv', 'both']
      }
   })

   // parameters whose control has gone away are reset, because the plot reads them whatever is
   // shown: 'col1' is computed from 'colorBy' for every plot type, and 'getLim' widens the axes
   // from the stored ellipse ranges whenever 'showEllipse' or 'confEllipse' say so, neither of
   // them re-checking the plot type. the other hidden parameters are read behind the same
   // condition which hides them, so they can keep their value
   $effect(() => {
      const p = params.scores;
      if (controls.scores.showEllipse.hiddenWhen(p)) p.showEllipse = false;
      if (controls.scores.colorBy.hiddenWhen(p))     p.colorBy = 'none';
      if (controls.scores.confEllipse.hiddenWhen(p)) p.confEllipse = 'none';
   });

   // combine main elements to be shared with all plots together
   const props = $derived({res, classres, model, simca, data, ncomp: simca.ncomp});


</script>

{#snippet buttons1(sel)}
   <Container label="Selected">
      <Button onclick={() => markOutlier(sel)} text="remove" class="destructive" />
   </Container>
{/snippet}

{#snippet buttons2(sel)}
   <Container label="Selected">
      <Button onclick={() => showContributions(sel)} text="plot" />
      <Button onclick={() => markOutlier(sel)} text="remove" class="destructive" />
   </Container>
{/snippet}


<PlotsRouter {plots} {controls} {props} bind:params={params} />
